import { Title } from '@app/components/core/typography/Title'
import { useTranslations } from '@i18n/useLanguage'
import { Drawer } from '@app/components/blocks/drawer/Drawer'
import { Style } from 'inlines'
import { CheckboxGroup } from '@app/components/core/checkbox/CheckboxGroup'
import { ListRegionModel } from 'functions/app/geography/list/region'
import { ServiceProviderSpecializationListModel } from 'functions/app/service-provider-specialization/list'
import { useState, useEffect } from 'react'
import Row from '@app/components/layout/Row'
import { Button } from '@app/components/core/button/Button'
import useMediaQuery from '@app/util/useMediaQuery'
import Column from '@app/components/layout/Column'
import { ServiceProviderFilter } from 'functions/app/service-provider/list/filters'
import { useQuery } from '@based/sdk'

export const ServiceProviderFilterDrawer = ({
  isOpen,
  onClose,
  filters,
  onFiltersChange,
}: {
  isOpen: boolean
  onClose: () => void
  filters: ServiceProviderFilter
  onFiltersChange: (filters: ServiceProviderFilter) => void
}) => {
  const {
    data: specializations,
  }: {
    data?: ServiceProviderSpecializationListModel[]
  } = useQuery('list-service-provider-specialization')
  const { data: regions }: { data?: ListRegionModel[] } =
    useQuery('list-region')
  const t = useTranslations().app

  const isMobile = useMediaQuery()

  const [selectedRegions, setSelectedRegions] = useState<number[]>([])
  const [selectedSpecializations, setSelectedSpecializations] = useState<
    number[]
  >([])

  useEffect(() => {
    if (isOpen) {
      setSelectedRegions(filters.regions ?? [])
      setSelectedSpecializations(filters.specializations ?? [])
    }
  }, [isOpen, filters.regions, filters.specializations])

  const toggleRegion = (region: number) => {
    setSelectedRegions((prev) =>
      prev.includes(region)
        ? prev.filter((r) => r !== region)
        : [...prev, region],
    )
  }

  const toggleSpecialization = (specialization: number) => {
    setSelectedSpecializations((prev) =>
      prev.includes(specialization)
        ? prev.filter((s) => s !== specialization)
        : [...prev, specialization],
    )
  }

  const handleApply = () => {
    onFiltersChange({
      regions: selectedRegions,
      specializations: selectedSpecializations,
    })
    onClose()
  }

  return (
    <Drawer
      title={t.discoverServiceProviders.filtersModal.title}
      open={isOpen}
      setOpen={onClose}
      side="left"
    >
      <Column
        style={{ height: '100%', flex: 0, justifyContent: 'space-between' }}
      >
        <div>
          <Title.H5 style={{ marginBottom: '1rem' }}>
            {t.discoverServiceProviders.filtersModal.specialism}
          </Title.H5>

          <SpecializationsCheckboxGroup
            selected={selectedSpecializations}
            onToggle={toggleSpecialization}
            style={{ marginBottom: '1.5rem' }}
            items={specializations || []}
          />

          <Title.H5 style={{ marginBottom: '1rem' }}>
            {t.discoverServiceProviders.filtersModal.operatingArea}
          </Title.H5>

          <RegionCheckboxGroup
            selected={selectedRegions}
            onToggle={toggleRegion}
            items={regions || []}
          />
        </div>

        <Row
          style={{
            width: '100%',
            justifyContent: isMobile ? 'space-between' : 'flex-end',
          }}
        >
          <Button onClick={onClose} label={t.common.back} variant="secondary" />
          <Button
            onClick={handleApply}
            label={t.discoverServiceProviders.filtersModal.apply}
          />
        </Row>
      </Column>
    </Drawer>
  )
}

interface FilterCheckboxGroupProps {
  selected: number[]
  onToggle: (value: number) => void
  style?: Style
}

interface RegionCheckboxGroupProps extends FilterCheckboxGroupProps {
  items: ListRegionModel[]
}

const RegionCheckboxGroup = ({
  selected,
  onToggle,
  items,
}: RegionCheckboxGroupProps) => {
  return (
    <CheckboxGroup
      options={items?.map((region) => ({
        label: region.name,
        value: region.id,
        key: region.code,
      }))}
      selected={selected}
      onToggle={onToggle}
      style={{ marginBottom: '0.5rem' }}
    />
  )
}

interface SpecializationsCheckboxGroupProps extends FilterCheckboxGroupProps {
  items: ServiceProviderSpecializationListModel[]
}

const SpecializationsCheckboxGroup = ({
  items,
  selected,
  onToggle,
  style,
}: SpecializationsCheckboxGroupProps) => {
  return (
    <CheckboxGroup
      options={items?.map((specialization) => ({
        label: specialization.name,
        value: specialization.id,
        key: specialization.id,
      }))}
      selected={selected}
      onToggle={onToggle}
      style={style}
    />
  )
}
